import {useState, useMemo, useEffect} from 'react';
import {
  Checkbox,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Skeleton,
  Toolbar,
  Typography,
  Box,
} from '@mui/material';

import { UserMetrics } from '@/api/dashboard';

import DashboardUserDialog from './DashboardUserDialog';

interface Props {
    users: UserMetrics[];
    isLoading: boolean;
    onProcessedUsersChange?: (users: UserMetrics[]) => void;
    showAllRows?: boolean;
}

type SortField = 'name' | 'status' | 'business_unit' | 'total_messages' | 'total_cost' | 'tokens_input' | 'tokens_output' | 'most_used_model';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'suspended';

const ROWS_PER_PAGE = 20;
const ALL_BUSINESS_UNITS = '__all_business_units__';

const DashboardUsersTable: React.FC<Props> = ({ users, isLoading, onProcessedUsersChange, showAllRows = false }) => {

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
    const [businessUnitFilter, setBusinessUnitFilter] = useState<string[]>([ALL_BUSINESS_UNITS]);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [sortBy, setSortBy] = useState<SortField>('total_messages');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserMetrics | null>(null);


    const businessUnitOptions = useMemo(() => {
      const units = new Set(users.map(u => u.business_unit || 'Unassigned'));
      return Array.from(units);
    }, [users]);


    const processedUsers = useMemo(() => {
      const filtered = users.filter((u) => {
        const bu = u.business_unit || 'Unassigned';
        const status = u.status || 'Unknown';
    
        const buOk = businessUnitFilter.includes(ALL_BUSINESS_UNITS) || businessUnitFilter.includes(bu);
        const statusOk = statusFilter === 'all' || status === statusFilter;
    
        return buOk && statusOk;
      });
    
      const numericFields = new Set(['total_messages', 'total_cost', 'tokens_input', 'tokens_output']);
      const isNumericField = (field: SortField): boolean => numericFields.has(field);
    
      const getSortValue = (u: UserMetrics): string | number => {
        if (sortBy === 'status') return u.status || 'Unknown';
        if (sortBy === 'business_unit') return u.business_unit || 'Unassigned';
        if (sortBy === 'total_messages') return u.total_messages || 0;
        if (sortBy === 'total_cost') return u.total_cost || 0;
        if (sortBy === 'tokens_input') return u.tokens_input || 0;
        if (sortBy === 'tokens_output') return u.tokens_output || 0;
        if (sortBy === 'most_used_model') return u.most_used_model || 'Unknown';
        return u.display_name || '';
      };
    
      return [...filtered].sort((a, b) => {
        const av = getSortValue(a);
        const bv = getSortValue(b);
        
        // Handle numeric comparison for numeric fields
        if (isNumericField(sortBy)) {
          const numA = typeof av === 'number' ? av : 0;
          const numB = typeof bv === 'number' ? bv : 0;
          return sortDirection === 'asc' ? numA - numB : numB - numA;
        }
        
        // Handle string comparison for string fields
        const strA = String(av).toLowerCase();
        const strB = String(bv).toLowerCase();
        if (strA < strB) return sortDirection === 'asc' ? -1 : 1;
        if (strA > strB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }, [users,businessUnitFilter, statusFilter, sortBy, sortDirection]);

    const pagedUsers = useMemo(() => {
      const start = page * rowsPerPage;
      return processedUsers.slice(start, start + rowsPerPage);
    }, [processedUsers, page, rowsPerPage]);

    // Use all rows in print mode, otherwise use paginated rows
    const displayedUsers = useMemo(() => {
      return showAllRows ? processedUsers : pagedUsers;
    }, [showAllRows, processedUsers, pagedUsers]);

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return;
        }
        setSortBy(field);
        setSortDirection('asc');
    };

    // Notify parent component of processed users
    useEffect(() => {
      onProcessedUsersChange?.(processedUsers);
    }, [processedUsers, onProcessedUsersChange]);

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Toolbar
            sx={[
              {
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
              }
            ]}
          >
            <Typography
                sx={{ flex: '1 1 100%' }}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                {processedUsers.length} total users
            </Typography>
          </Toolbar>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mb: 1 }}>
            {/* Business Unit filter */}
            <TextField
              select
              size="small"
              label="Business Unit"
              value={businessUnitFilter}
              onChange={(e) => {
                const value = e.target.value;
                const selected = typeof value === 'string' ? value.split(',') : value;
                            
                // Case 1: User is selecting ALL from specific items
                if (selected.includes(ALL_BUSINESS_UNITS) && !businessUnitFilter.includes(ALL_BUSINESS_UNITS)) {
                  setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                }
                // Case 2: User is deselecting ALL
                else if (!selected.includes(ALL_BUSINESS_UNITS) && businessUnitFilter.includes(ALL_BUSINESS_UNITS)) {
                  if (selected.length > 0) {
                    setBusinessUnitFilter(selected);
                  } else {
                    setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                  }
                }
                // Case 3: User is adding specific items while ALL is selected
                else if (businessUnitFilter.includes(ALL_BUSINESS_UNITS) && selected.includes(ALL_BUSINESS_UNITS) && selected.length > 1) {
                  // Remove ALL and keep only specific items
                  const specificItems = selected.filter(s => s !== ALL_BUSINESS_UNITS);
                  setBusinessUnitFilter(specificItems);
                }
                // Case 4: Normal multi-select or empty
                  else if (selected.length === 0) {
                  setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                } else {
                    setBusinessUnitFilter(selected);
                }
              }}
              sx={{ width: 250 }}
              slotProps={{
                select: {
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const selectedBusinessUnits = selected as string[];
                    if (selectedBusinessUnits.includes(ALL_BUSINESS_UNITS)) {
                      return 'All';
                    }
                    return selectedBusinessUnits.join(', ');
                  }
                }
              }}
            >
              <MenuItem value={ALL_BUSINESS_UNITS}>
                <Checkbox checked={businessUnitFilter.includes(ALL_BUSINESS_UNITS)} />
                <ListItemText primary="All" />
              </MenuItem>
              {businessUnitOptions.map((bu) => (
                <MenuItem key={bu} value={bu}>
                  <Checkbox
                    checked={!businessUnitFilter.includes(ALL_BUSINESS_UNITS) && businessUnitFilter.includes(bu)}
                  />
                  <ListItemText primary={bu} />
                </MenuItem>
              ))}
            </TextField>

            {/* Status filter */}
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Suspended">Suspended</MenuItem>
            </TextField>
          </Box>
        </Box>
        <TableContainer>  
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'business_unit'}
                      direction={sortBy === 'business_unit' ? sortDirection : 'asc'}
                      onClick={() => handleSort('business_unit')}
                    >
                      Business Unit
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'total_messages'}
                      direction={sortBy === 'total_messages' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total_messages')}
                    >
                      Total Messages
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'total_cost'}
                      direction={sortBy === 'total_cost' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total_cost')}
                    >
                      Total Cost
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'most_used_model'}
                      direction={sortBy === 'most_used_model' ? sortDirection : 'asc'}
                      onClick={() => handleSort('most_used_model')}
                    >
                      Most Used Model
                    </TableSortLabel></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? 
                  new Array(rowsPerPage).fill(null).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6} align='center'>
                      <Skeleton variant="text" width="100%" />
                    </TableCell>
                  </TableRow>)) : !isLoading && displayedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : displayedUsers.map((user) => (
                  <TableRow key={user.user_id} onClick={() => {
                    setSelectedUser(user);
                    setDetailsDialogOpen(true);
                  }} sx={{ cursor: 'pointer' }}>
                    <TableCell>{user.display_name || 'Unknown'}</TableCell>
                    <TableCell>{user.status || 'Unknown'}</TableCell>
                    <TableCell>{user.business_unit || 'Unknown'}</TableCell>
                    <TableCell>{user.total_messages || 0}</TableCell>
                    <TableCell>{user.total_cost || 0}</TableCell>
                    <TableCell>{user.most_used_model || 'Unknown'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!showAllRows && (
            <TablePagination
              component="div"
              count={users.length}
              page={page}
              showFirstButton={true}
              showLastButton={true}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[15,20,25]}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
            )}
        </TableContainer>
        <DashboardUserDialog user={selectedUser} onClose={() => setDetailsDialogOpen(false)} detailsDialogOpen={detailsDialogOpen} />
      </Box>
      
  )
}

export default DashboardUsersTable;